import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../Common/Configurations/AuthContext';
import apiService from '../../Common/Configurations/APIService';
import config from '../../Common/Configurations/APIConfig';
import countries from '../../Data/Countries.json';
import currencies from '../../Data/Currency.json';
import './MyProfile.css';

const EditProfileForm = () => {
    const { user } = useAuth();
    const [companyData, setCompanyData] = useState({});
    const [editedData, setEditedData] = useState({});
    const [editMode, setEditMode] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [apiError, setApiError] = useState('');
    const fileInputRef = useRef(null);

    useEffect(() => {
        const getData = async () => {
            if (!user?.id) { setIsLoading(false); return; }
            setIsLoading(true);
            try {
                const res = await apiService.getAll(config.GETCOMPANYBYUSER + '/' + user.id);
                setCompanyData(res);
                setEditedData(res);
                setLogoPreview(res.logoUrl);
            } catch (err) {
                setApiError('Failed to fetch profile data.');
                console.error('Error fetching company data', err);
            } finally {
                setIsLoading(false);
            }
        };
        getData();
    }, [user]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setEditedData({ ...editedData, [name]: value });
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setLogoFile(file);
            setLogoPreview(URL.createObjectURL(file));
        }
    };

    const startEdit = () => {
        setEditedData({ ...companyData });
        setEditMode(true);
    };

    const cancelEdit = () => {
        setEditMode(false);
        setEditedData(companyData);
        setLogoFile(null);
        setLogoPreview(companyData.logoUrl);
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        setApiError('');
        const formData = new FormData();
        formData.append('companyId', user?.cid);
        formData.append('companyName', editedData.companyName);
        formData.append('brandName', editedData.brandName || '');
        formData.append('email', editedData.email);
        formData.append('phoneNumber', editedData.phoneNumber);
        formData.append('gstNo', editedData.gstNo || '');
        formData.append('panCard', editedData.panCard || '');
        formData.append('address', editedData.address || '');
        formData.append('city', editedData.city || '');
        formData.append('state', editedData.state || '');
        formData.append('country', editedData.country);
        formData.append('currency', editedData.currency);
        formData.append('isActive', editedData.isActive);
        formData.append('planType', editedData.planType || companyData.planType || '');
        if (logoFile) { formData.append('logoFile', logoFile); }
        try {
            await apiService.update(config.UPDATECOMPANY, formData);
            setEditMode(false);
            const res = await apiService.getAll(config.GETCOMPANYBYUSER + '/' + user.id);
            setCompanyData(res);
            setEditedData(res);
            setLogoFile(null);
            setLogoPreview(res.logoUrl);
        } catch (err) {
            setApiError('Failed to update profile.');
        } finally {
            setIsSaving(false);
        }
    };

    const data = editMode ? editedData : companyData;

    const Field = ({ label, name, type = 'text', readOnly }) => (
        <div className="mb-3">
            <label className="profile-field-label">{label}</label>
            {!editMode ? (
                <div className="profile-field-value">{data[name] || <span className="text-muted">—</span>}</div>
            ) : (
                <input
                    type={type}
                    name={name}
                    value={data[name] || ''}
                    onChange={handleInputChange}
                    className="profile-form-control"
                    readOnly={readOnly}
                />
            )}
        </div>
    );

    if (isLoading) {
        return (
            <div className="profile-card-modern position-relative" style={{ minHeight: 300 }}>
                <div className="form-loader-overlay">
                    <div className="spinner-border text-primary" />
                </div>
            </div>
        );
    }

    return (
        <div className="profile-card-modern position-relative">
            {/* Header: Logo + Company Name + Status */}
            <div className="profile-card-header">
                <div className="profile-logo-wrap">
                    <img
                        src={logoPreview || '/assets/images/placeholder-logo.png'}
                        alt="Agency Logo"
                        className="profile-logo-img"
                    />
                    {editMode && (
                        <>
                            <button
                                type="button"
                                className="logo-upload-btn"
                                title="Change logo"
                                onClick={() => fileInputRef.current?.click()}
                            >
                                ✎
                            </button>
                            <input
                                ref={fileInputRef}
                                type="file"
                                className="logo-file-input"
                                onChange={handleFileChange}
                                accept="image/*"
                            />
                        </>
                    )}
                </div>
                <div className="profile-header-info">
                    <p className="profile-company-name">{companyData.companyName || 'Your Agency'}</p>
                    <p className="profile-brand-name">{companyData.brandName || companyData.email || ''}</p>
                </div>
                <span className={`profile-status-badge ${companyData.isActive ? 'active' : 'inactive'}`}>
                    {companyData.isActive ? 'Active' : 'Inactive'}
                </span>
            </div>

            <form onSubmit={handleUpdate} noValidate>
                {apiError && <div className="alert alert-danger mx-4 mt-3 mb-0">{apiError}</div>}

                {/* Company Information */}
                <div className="profile-section">
                    <p className="profile-section-title">Company Information</p>
                    <div className="row">
                        <div className="col-md-6"><Field label="Agency Name" name="companyName" /></div>
                        <div className="col-md-6"><Field label="Brand Name" name="brandName" /></div>
                        <div className="col-md-6">
                            <div className="mb-3">
                                <label className="profile-field-label">GST No.</label>
                                {!editMode ? (
                                    <div className="profile-field-value">{data.gstNo || <span className="text-muted">—</span>}</div>
                                ) : (
                                    <input type="text" name="gstNo" value={data.gstNo || ''} onChange={handleInputChange} className="profile-form-control" />
                                )}
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="mb-3">
                                <label className="profile-field-label">PAN Card</label>
                                {!editMode ? (
                                    <div className="profile-field-value">{data.panCard || <span className="text-muted">—</span>}</div>
                                ) : (
                                    <input type="text" name="panCard" value={data.panCard || ''} onChange={handleInputChange} className="profile-form-control" />
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Contact Details */}
                <div className="profile-section">
                    <p className="profile-section-title">Contact Details</p>
                    <div className="row">
                        <div className="col-md-6"><Field label="Email Address" name="email" type="email" /></div>
                        <div className="col-md-6"><Field label="Phone Number" name="phoneNumber" type="tel" /></div>
                    </div>
                </div>

                {/* Address & Financial */}
                <div className="profile-section">
                    <p className="profile-section-title">Address &amp; Financial</p>
                    <div className="row">
                        <div className="col-12"><Field label="Address" name="address" /></div>
                        <div className="col-md-4"><Field label="City" name="city" /></div>
                        <div className="col-md-4"><Field label="State" name="state" /></div>
                        <div className="col-md-4">
                            <div className="mb-3">
                                <label className="profile-field-label">Country</label>
                                {!editMode ? (
                                    <div className="profile-field-value">{data.country || <span className="text-muted">—</span>}</div>
                                ) : (
                                    <select name="country" value={data.country || ''} onChange={handleInputChange} className="profile-form-control">
                                        {countries.map(c => <option key={c.code} value={c.name}>{c.name}</option>)}
                                    </select>
                                )}
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="mb-3">
                                <label className="profile-field-label">Currency</label>
                                {!editMode ? (
                                    <div className="profile-field-value">{data.currency || <span className="text-muted">—</span>}</div>
                                ) : (
                                    <select name="currency" value={data.currency || ''} onChange={handleInputChange} className="profile-form-control">
                                        {currencies.map(c => <option key={c.code} value={c.code}>{c.name} ({c.code})</option>)}
                                    </select>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer Actions */}
                <div className="profile-card-footer">
                    {!editMode ? (
                        <button type="button" className="btn-profile-edit" onClick={startEdit}>
                            Edit Profile
                        </button>
                    ) : (
                        <>
                            <button type="button" className="btn-profile-cancel" onClick={cancelEdit} disabled={isSaving}>
                                Cancel
                            </button>
                            <button type="submit" className="btn-profile-save" disabled={isSaving}>
                                {isSaving ? 'Saving…' : 'Save Changes'}
                            </button>
                        </>
                    )}
                </div>
            </form>
        </div>
    );
};

export default EditProfileForm;
